import { clientApi } from "@/utils/api";

import { TGetMeDto, TGetMeResDto, TMeResDto, TProductAccountFlags } from "@/api/services/admin/auth/dtos/get-me.dto";
import {
  TImpersonationInitUserDto,
  TImpersonationInitUserReqDto,
  TImpersonationInitUserResDto,
  TImpersonationLoginDto,
  TImpersonationLoginReqDto,
  TImpersonationLoginResDto,
  TImpersonationVerifyLoginDto,
  TImpersonationVerifyLoginReqDto,
  TImpersonationVerifyLoginResDto,
} from "@/api/services/admin/auth/dtos/impersonation-login.dto";
import { TLogoutReqDto, TLogoutResDto } from "@/api/services/admin/auth/dtos/logout.dto";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/admin/auth/dtos/refresh-token.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EProductCode } from "@/types/enum";
import { setToken } from "@/utils/token";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

/**
 * 各プロダクトについて、ログイン中の管理者が外部アカウントを連携済みかを判定するリゾルバ。
 * 新しいプロダクトのフラグを増やす場合はここに1エントリ追加するだけでよい（他は変更不要）。
 */
const PRODUCT_ACCOUNT_RESOLVERS: Partial<Record<EProductCode, (me: TGetMeDto) => boolean>> = {
  [EProductCode.VISA]: (me) => Boolean(me.visaAdminUserId),
  [EProductCode.ACCOUNT]: (me) => Boolean(me.accountAdminUserId),
};

const resolveProductAccounts = (me: TGetMeDto): TProductAccountFlags =>
  (Object.entries(PRODUCT_ACCOUNT_RESOLVERS) as [EProductCode, (me: TGetMeDto) => boolean][]).reduce<TProductAccountFlags>((acc, [code, resolve]) => {
    acc[code] = resolve(me);
    return acc;
  }, {});

export const useMe = (options?: Omit<UseQueryOptions<TGetMeResDto, Error, TMeResDto>, "queryKey" | "queryFn" | "select">) => {
  return useQuery<TGetMeResDto, Error, TMeResDto>({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
    ...options,
    select: (res) => {
      const productAccounts = resolveProductAccounts(res.data);
      return {
        ...res,
        data: {
          ...res.data,
          productAccounts,
        },
      };
    },
  });
};

export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  const rs = await clientApi.post<TRefreshTokenResDto>("/refresh-token", payload);
  const { data } = rs.data;
  setToken(data.accessToken, data.refreshToken);
  return rs.data;
};

export const logoutApi = async (payload?: TLogoutReqDto): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout", payload);
  return rs?.data;
};

export const impersonationLoginApi = async (payload: TImpersonationLoginReqDto): Promise<TImpersonationLoginDto> => {
  const rs = await clientApi.post<TImpersonationLoginResDto>("/impersonation/login", payload);
  return rs.data.data;
};

export const impersonationVerifyLoginApi = async (payload: TImpersonationVerifyLoginReqDto): Promise<TImpersonationVerifyLoginDto> => {
  const rs = await clientApi.post<TImpersonationVerifyLoginResDto>("/impersonation/verify-login", payload);
  return rs.data.data;
};

export const impersonationInitUserApi = async (payload: TImpersonationInitUserReqDto): Promise<TImpersonationInitUserDto> => {
  const rs = await clientApi.post<TImpersonationInitUserResDto>("/impersonation/init-user", payload);
  return rs.data.data;
};
